import type { FlightLeg } from "./types";

const STORAGE_KEY = "flights_airport_use_v1";

/** Frequencies persisted in the browser (blur / picks). */
export function getStoredAirportFrequencies(): Map<string, number> {
  return loadMap();
}

function loadMap(): Map<string, number> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Map();
    const o = JSON.parse(raw) as Record<string, number>;
    const m = new Map<string, number>();
    for (const [k, v] of Object.entries(o)) {
      const c = normIcao(k);
      if (c) m.set(c, (m.get(c) ?? 0) + (Number(v) || 0));
    }
    return m;
  } catch {
    return new Map();
  }
}

function saveMap(m: Map<string, number>): void {
  const o: Record<string, number> = {};
  for (const [k, v] of m) o[k] = v;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(o));
  } catch {
    /* ignore quota */
  }
}

export function normIcao(s: string): string {
  return s.trim().toUpperCase().replace(/\s+/g, "");
}

/** True if value looks like a plausible ICAO / airport code for logging use. */
export function isPlausibleAirportCode(s: string): boolean {
  const c = normIcao(s);
  return /^[A-Z0-9]{3,8}$/.test(c);
}

export function bumpAirportUse(code: string, delta = 1): void {
  const c = normIcao(code);
  if (!isPlausibleAirportCode(c)) return;
  const m = loadMap();
  m.set(c, (m.get(c) ?? 0) + delta);
  saveMap(m);
}

export function frequenciesFromLegs(legs: FlightLeg[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const leg of legs) {
    const f = normIcao(leg.fromIcao);
    const t = normIcao(leg.toIcao);
    if (isPlausibleAirportCode(f)) m.set(f, (m.get(f) ?? 0) + 1);
    if (isPlausibleAirportCode(t)) m.set(t, (m.get(t) ?? 0) + 1);
  }
  return m;
}

export function mergeFrequencyMaps(maps: Map<string, number>[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const m of maps) {
    for (const [k, v] of m) {
      if (!isPlausibleAirportCode(k)) continue;
      out.set(k, (out.get(k) ?? 0) + v);
    }
  }
  return out;
}

export type AirportOption = { icao: string; count: number };

export function mapToSortedOptions(m: Map<string, number>): AirportOption[] {
  return [...m.entries()]
    .map(([icao, count]) => ({ icao, count }))
    .sort((a, b) => b.count - a.count || a.icao.localeCompare(b.icao));
}

export function filterAirportOptions(
  options: AirportOption[],
  rawQuery: string,
  limit = 15
): AirportOption[] {
  const q = normIcao(rawQuery);
  if (!q) return options.slice(0, limit);
  const filtered = options.filter(({ icao }) => icao.includes(q));
  filtered.sort((a, b) => {
    const ap = a.icao.startsWith(q) ? 0 : 1;
    const bp = b.icao.startsWith(q) ? 0 : 1;
    if (ap !== bp) return ap - bp;
    if (b.count !== a.count) return b.count - a.count;
    return a.icao.localeCompare(b.icao);
  });
  return filtered.slice(0, limit);
}
